import { ref, watch, computed } from "vue";
import type { ISatMapProps } from "@chatfood/design-system";
import { useNavigation } from "../use-navigation";
import { useLocale } from "../use-locale";
import type { IDomainCustomerCoveredAddress } from "~/domain/customer-covered-address";
import type { IDomainNewAddress } from "~/domain/new-address";
import type { IDomainNewUncoveredAddress } from "~/domain/new-uncovered-address";
import type { IDomainCoordinate } from "~/domain/coordinate";
import type { IDomainOutletCoverage } from "~/domain/outlet-coverage";
import type {
  DeliveryAddress,
  DeliveryLocation,
} from "~/domain/delivery-location";
import type { IDomainOrderScheduledTime } from "~/domain/order-scheduled-time";
import { OUTLET_STATES } from "~/domain/outlet-state";
import { useBusinessStore } from "~/stores/business";
import { useBasketStore } from "~/stores/basket";
import { useCustomerStore } from "~/stores/customer";
import { useGuestStore } from "~/stores/guest";
import { useModalHubStore } from "~/components/modal-hub";
import { useDirectBackend } from "#imports";

export const useDelivery = () => {
  const { push } = useNavigation();
  const { t } = useLocale();

  const business = useBusinessStore();
  const basket = useBasketStore();
  const customer = useCustomerStore();
  const guest = useGuestStore();
  const modalHub = useModalHubStore();

  const { outletRepo } = useDirectBackend();

  const isCheckingOutletsAvailability = ref(false);
  const isLoadingDelivery = ref(false);
  const deliveryAddress = ref<IDomainCustomerCoveredAddress>();
  const deliveryLocation = ref<
    IDomainNewAddress | IDomainNewUncoveredAddress
  >();
  const currentGeoLocation = ref<IDomainCoordinate>();
  const outletsAvailableForLocation = ref<IDomainOutletCoverage[]>([]);

  const mapBoundaries = ref<ISatMapProps["mapBounds"]>({
    country: business.country,
  });

  const fetchOutletsForCoordinates = async (coordinates: IDomainCoordinate) => {
    try {
      isCheckingOutletsAvailability.value = true;
      const outlets = await outletRepo.getCoverages({
        businessId: business.id,
        salesChannel: business.salesChannel,
        lat: coordinates.lat,
        lng: coordinates.lng,
      });
      outletsAvailableForLocation.value = outlets;
    } catch {
      outletsAvailableForLocation.value = [];
    } finally {
      isCheckingOutletsAvailability.value = false;
    }
  };

  const onDeliveryLocationSelected = (
    location: IDomainNewAddress,
    skipRefocus = false,
  ) => {
    deliveryAddress.value = undefined;
    deliveryLocation.value = location;

    if (!skipRefocus) {
      mapBoundaries.value = {
        coordinates: {
          lat: location.coordinates.lat,
          lng: location.coordinates.lng,
        },
      };
    }
  };

  const setDeliveryLocationCoordinates = (coordinates: IDomainCoordinate) => {
    if (deliveryLocation.value) {
      deliveryLocation.value = {
        ...deliveryLocation.value,
        coordinates,
      };
    }
  };

  const onAddressSelected = (
    address: IDomainCustomerCoveredAddress,
    skipRefocus = false,
  ) => {
    deliveryLocation.value = undefined;
    deliveryAddress.value = address;

    if (!skipRefocus) {
      mapBoundaries.value = {
        coordinates: {
          lat: address.lat,
          lng: address.lng,
        },
      };
    }
  };

  const onRefocusRegion = (region: string) => {
    mapBoundaries.value = {
      location: region,
    };
  };

  const updateMapBoundariesWithGeoLocation = () => {
    if (currentGeoLocation.value) {
      mapBoundaries.value = {
        ...mapBoundaries.value,
        coordinates: currentGeoLocation.value,
      };
    }
  };

  const setCurrentGeoLocation = (coordinate?: IDomainCoordinate) => {
    currentGeoLocation.value = coordinate;
    updateMapBoundariesWithGeoLocation();
  };

  // Watch delivery location coordinates and fetch outlets when they change
  watch(
    () => deliveryLocation.value?.coordinates,
    (coordinates) => {
      if (coordinates) {
        fetchOutletsForCoordinates(coordinates);
      }
    },
  );

  // Watch delivery address coordinates and fetch outlets when they change
  watch(
    () =>
      deliveryAddress.value && {
        lat: deliveryAddress.value.lat,
        lng: deliveryAddress.value.lng,
      },
    (coordinates) => {
      if (coordinates) {
        fetchOutletsForCoordinates(coordinates);
      }
    },
  );

  const onDeliverTo = async (
    location: Omit<DeliveryAddress, "type"> | Omit<DeliveryLocation, "type">,
  ) => {
    const coverages = outletsAvailableForLocation.value;

    if (!coverages.length) return;

    // Single open outlet → proceed directly (no modal)
    if (coverages.length === 1 && coverages[0].state !== OUTLET_STATES.CLOSED) {
      await proceedWithDelivery(coverages[0], location);
      return;
    }

    // 1 closed outlet or multiple outlets → show modal
    isLoadingDelivery.value = false;
    modalHub.showOutletSelection(
      coverages,
      async (selectedOutlet: IDomainOutletCoverage) =>
        await proceedWithDelivery(selectedOutlet, location),
    );
  };

  const proceedWithDelivery = async (
    outlet: IDomainOutletCoverage,
    location: Omit<DeliveryAddress, "type"> | Omit<DeliveryLocation, "type">,
    scheduledTime?: IDomainOrderScheduledTime,
  ) => {
    try {
      isLoadingDelivery.value = !modalHub.isOutletSelectionOn;

      const hasExistingBasket = basket.summaries.some(
        (s) =>
          s.outletId === outlet.id &&
          s.orderingMode === "delivery" &&
          s.salesChannel === business.salesChannel,
      );

      const shouldDeferCreation = outlet.state === OUTLET_STATES.PRE_ORDER_ONLY;

      if (shouldDeferCreation && !hasExistingBasket) {
        basket.setPendingDeliveryLocation(location);
      } else {
        await basket.loadBasketWithLocation({
          businessId: business.id,
          currency: business.currency,
          outletId: outlet.id,
          orderingMode: "delivery",
          salesChannel: business.salesChannel,
          location,
          ...(scheduledTime && { scheduledTime }),
        });

        if (!customer.isLogged) {
          guest.removeAddress();
        }
      }

      await push("outlet", {
        orderingMode: "delivery",
        outletSlug: outlet.slug,
      });
    } catch (e) {
      isLoadingDelivery.value = false;
      throw e;
    }
  };

  const getDeliverButtonState = computed(() => {
    if (isCheckingOutletsAvailability.value) {
      return {
        disabled: true,
        label: t("pages.select_mode.marker_label.checking_availability"),
      };
    }

    if (outletsAvailableForLocation.value.length === 0) {
      return {
        disabled: true,
        label: t("pages.select_mode.marker_label.no_coverage"),
      };
    }

    return {
      disabled: false,
      label: t("pages.select_mode.marker_label.deliver_here"),
    };
  });

  return {
    mapBoundaries,
    isCheckingOutletsAvailability,
    isLoadingDelivery,
    deliveryAddress,
    deliveryLocation,
    currentGeoLocation,
    outletsAvailableForLocation,
    onDeliveryLocationSelected,
    onAddressSelected,
    onRefocusRegion,
    onDeliverTo,
    proceedWithDelivery,
    fetchOutletsForCoordinates,
    setCurrentGeoLocation,
    setDeliveryLocationCoordinates,
    getDeliverButtonState,
  };
};
